'use client';

import { merge } from 'es-toolkit';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';

import { RouterLink } from 'src/routes/components';
import { paths, newSearchParams } from 'src/routes/paths';

import { useTranslate } from 'src/locales';

import { Logo } from 'src/components/logo';
import { ProductSearch } from 'src/components/search';

import { FooterMobile } from '../main/footer';
import { SimpleCompactContent } from './content';
import { NavDesktop } from '../main/nav/desktop';
import { MainSection } from '../core/main-section';
import { LayoutSection } from '../core/layout-section';
import { HeaderSection } from '../core/header-section';
import { ThemeButton } from '../components/theme-button';
import { navData as mainNavData } from '../nav-config-main';
// import { SettingsButton } from '../components/settings-button';
import { LanguagePopover } from '../components/language-popover';
import { ShoppingCartButton } from '../components/shopping-cart-button';

// ----------------------------------------------------------------------

export function SimpleLayout({ sx, cssVars, children, slotProps, layoutQuery = 'md' }) {
  const { t, currentLang } = useTranslate();

  const navData = slotProps?.nav?.data ?? mainNavData;

  const renderHeader = () => {
    const headerSlotProps = { container: { sx: { gap: 6 } } };

    const headerSlots = {
      topArea: (
        <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
          This is an info Alert.
        </Alert>
      ),
      leftArea: (
        <>
          {/** @slot Logo */}
          <Logo />

          {/** @slot Nav desktop */}
          <NavDesktop data={navData} sx={{ display: { xs: 'none', [layoutQuery]: 'flex' } }} />
        </>
      ),
      centerArea: <ProductSearch />,
      rightArea: (
        <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
          {/** @slot Help link */}
          <Link
            color="inherit"
            variant="subtitle2"
            component={RouterLink}
            href={`${paths.faqs}${newSearchParams({ country: currentLang.countryName })}`}
          >
            {t('Need help?')}
          </Link>

          {/** @slot Settings button */}
          {/* <SettingsButton /> */}

          {/** @slot Theme button */}
          <ThemeButton />

          {/** @slot Language popover */}
          <LanguagePopover />

          {/** @slot Cart popover */}
          <ShoppingCartButton />
        </Box>
      ),
    };

    return (
      <HeaderSection
        disableElevation
        layoutQuery={layoutQuery}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={[
          {
            position: { [layoutQuery]: 'fixed' },
            display: { xs: 'none', [layoutQuery]: 'flex' },
          },
          ...(Array.isArray(slotProps?.header?.sx)
            ? (slotProps?.header?.sx ?? [])
            : [slotProps?.header?.sx]),
        ]}
      />
    );
  };

  const renderFooter = () => <FooterMobile sx={{ display: { [layoutQuery]: 'none' } }} />;

  const renderMain = () => {
    const { compact, ...restContentProps } = slotProps?.content ?? {};

    return (
      <MainSection
        {...slotProps?.main}
        sx={[
          (theme) => ({
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: theme.spacing(0, 2), [layoutQuery]: 0 },
          }),
          ...(Array.isArray(slotProps?.main?.sx)
            ? (slotProps?.main?.sx ?? [])
            : [slotProps?.main?.sx]),
        ]}
      >
        {compact ? (
          <SimpleCompactContent
            layoutQuery={layoutQuery}
            {...restContentProps}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {children}
          </SimpleCompactContent>
        ) : (
          children
        )}
      </MainSection>
    );
  };

  return (
    <LayoutSection
      /** @Header */
      headerSection={renderHeader()}
      /** @Footer */
      footerSection={renderFooter()}
      /** @Styles */
      cssVars={{ '--layout-simple-content-compact-width': '448px', ...cssVars }}
      sx={[...(Array.isArray(sx) ? sx : [sx])]}
    >
      {renderMain()}
    </LayoutSection>
  );
}
